import React from 'react';
import Loader from 'react-loader';

import { ROOT_REGION_ID } from 'upstream-shared/constants';
import { Operator, OperatorRegionYear } from 'upstream-shared/models';

import { useAppSelector, useProductionUnits } from '../../../hooks';
import { getChartMinYear, range } from '../../../utils';
import ProductionChart from '../../Production/components/ProductionChart';
import ErrorComponent from '../../AsyncPage/ErrorComponent';

interface OperatorProductionChartProps {
  storeIdentifier: string;
  regionId: number;
  dataField: string;
  yTitle: string;
  h: number;
  color?: string;
  minYear?: number;
  yearWindow?: number;
  lastFullYear?: number;
}

const OperatorProductionChart = (props: OperatorProductionChartProps) => {
  const { storeIdentifier, dataField, h, yTitle, color, regionId, lastFullYear } = props;
  const error: string | undefined = useAppSelector((state) => state.serverStores?.[storeIdentifier]?.error);
  const isFetching: boolean = useAppSelector((state) => state.serverStores?.[storeIdentifier]?.isFetching);
  const operator: Operator = useAppSelector((state) => state.serverStores?.[storeIdentifier]?.payload);

  const { convertToSelectedUnits } = useProductionUnits();

  if (isFetching) return <Loader loaded={false} />;
  if (error) return <ErrorComponent error={error} />;
  if (!operator) return null;

  const prodYearField = 'prodYear' + (regionId >= ROOT_REGION_ID ? 'Region' : regionId < 100 ? 'State' : 'County');
  const operatorRegionYearData = (operator[prodYearField] || []).filter((o: OperatorRegionYear) => o.regionId === regionId);

  if (operatorRegionYearData.length < 1) {
    return <h5>No data.</h5>;
  }

  const years = operatorRegionYearData.map((o: OperatorRegionYear) => o.year);
  const minEffectiveYear = getChartMinYear(years, props.minYear, props.yearWindow);
  const maxDataYear = years.length > 0 ? Math.max(...years) : new Date().getFullYear();
  const allYears = range(minEffectiveYear ?? Math.min(...years), maxDataYear);

  const plotData = allYears.map((year) => {
    const ory = operatorRegionYearData.find((o: OperatorRegionYear) => o.year === year);
    const rawValue = ory && Math.round(Math.max(0, ory[dataField]));
    return ory
      ? {
        ...ory,
        x: ory.year,
        y: dataField === 'wellCount' ? rawValue : convertToSelectedUnits(rawValue),
      }
      : {
        x: year,
        y: 0,
      };
  });

  const hasValues = plotData.find((t: any) => t.y > 0);
  if (!hasValues) return <h5>{minEffectiveYear ? `No data from ${minEffectiveYear} to present.` : 'No data present.'}</h5>;

  return <ProductionChart lastFullYear={lastFullYear} seriesData={plotData!} yTitle={yTitle} h={h} color={color} />;
};

export default OperatorProductionChart;
